// FE-SCRM-060 — <TagsInput> chip-style input with autocomplete.
//
// Example:
//
//     <TagsInput
//       value={prospect.tags}
//       onChange={(next) => setTags(next)}
//       fetchSuggestions={(q) => api.tagSuggestions(q, "prospect")}
//       placeholder="Add a tag…"
//     />
//
// Used by AddProspectModal, Prospect detail inline-edit, Opportunity
// detail tag section, ContactDetailPage tag section. The
// ``fetchSuggestions`` prop is injected so the host page can decide
// the entity_type — keeps this component free of API-client knowledge.

import {
  useCallback, useEffect, useRef, useState,
} from "react";


export interface TagSuggestion {
  tag: string;
  count: number;
}


export interface TagsInputProps {
  value: readonly string[];
  onChange: (next: string[]) => void;
  fetchSuggestions: (q: string) => Promise<TagSuggestion[]>;
  placeholder?: string;
  /** Debounce for the suggestion query. */
  debounceMs?: number;
  disabled?: boolean;
}


const DEBOUNCE_MS = 250;


function _norm(s: string): string {
  return s.trim().toLowerCase();
}


export function TagsInput({
  value, onChange, fetchSuggestions,
  placeholder = "Add a tag…",
  debounceMs = DEBOUNCE_MS,
  disabled,
}: TagsInputProps): JSX.Element {
  const [text, setText] = useState("");
  const [suggestions, setSuggestions] = useState<TagSuggestion[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelled = useRef(false);

  // React 18 StrictMode runs effect cleanups immediately after the
  // first mount in dev — without resetting the ref in setup, the
  // strict-mode cleanup pins ``cancelled.current = true`` for the
  // life of the component, swallowing every setSuggestions call.
  // Re-arm on every (re)mount.
  useEffect(() => {
    cancelled.current = false;
    return () => { cancelled.current = true; };
  }, []);

  const addTag = useCallback((raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    const seen = new Set(value.map(_norm));
    if (seen.has(_norm(trimmed))) {
      setText("");
      setSuggestions([]);
      return;
    }
    onChange([...value, trimmed]);
    setText("");
    setSuggestions([]);
  }, [value, onChange]);

  const removeAt = useCallback((idx: number) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
  }, [value, onChange]);

  const onChangeText = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setText(next);
    if (timer.current) clearTimeout(timer.current);
    if (!next.trim()) {
      setSuggestions([]);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const rows = await fetchSuggestions(next.trim());
        if (!cancelled.current) setSuggestions(rows);
      } catch {
        // Network errors leave the dropdown empty; the host modal
        // surfaces its own banner if it cares.
        if (!cancelled.current) setSuggestions([]);
      }
    }, debounceMs);
  }, [debounceMs, fetchSuggestions]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(text);
      return;
    }
    if (e.key === "Backspace" && text === "" && value.length > 0) {
      e.preventDefault();
      removeAt(value.length - 1);
    }
  }, [text, value, addTag, removeAt]);

  return (
    <div
      className={
        "flex flex-wrap items-center gap-1 rounded-md border "
        + "border-gray-300 bg-white px-2 py-1.5 "
        + "focus-within:border-indigo-500 focus-within:ring-1 "
        + "focus-within:ring-indigo-500"
      }
    >
      {value.map((tag, idx) => (
        <span
          key={`${tag}-${idx}`}
          className={
            "inline-flex items-center gap-1 rounded-full bg-indigo-50 "
            + "px-2 py-0.5 text-xs font-medium text-indigo-700"
          }
        >
          {tag}
          <button
            type="button"
            aria-label={`Remove tag ${tag}`}
            disabled={disabled}
            onClick={() => removeAt(idx)}
            className={
              "rounded-full p-0.5 text-indigo-500 hover:bg-indigo-100 "
              + "hover:text-indigo-800 disabled:cursor-not-allowed"
            }
          >
            ×
          </button>
        </span>
      ))}
      <div className="relative flex-1 min-w-[6rem]">
        <input
          role="combobox"
          type="text"
          value={text}
          onChange={onChangeText}
          onKeyDown={onKeyDown}
          placeholder={value.length === 0 ? placeholder : ""}
          disabled={disabled}
          aria-expanded={suggestions.length > 0}
          aria-autocomplete="list"
          className={
            "w-full border-none p-0 text-sm placeholder-gray-400 "
            + "focus:outline-none focus:ring-0 disabled:bg-transparent"
          }
        />
        {suggestions.length > 0 && (
          <ul
            role="listbox"
            className={
              "absolute left-0 right-0 top-full z-10 mt-1 max-h-60 "
              + "overflow-auto rounded-md border border-gray-200 "
              + "bg-white py-1 shadow-lg"
            }
          >
            {suggestions.map((s) => (
              <li
                key={s.tag}
                role="option"
                aria-selected={false}
                onMouseDown={(e) => {
                  e.preventDefault();
                  addTag(s.tag);
                }}
                className={
                  "flex cursor-pointer items-center justify-between "
                  + "px-3 py-1.5 text-sm hover:bg-indigo-50"
                }
              >
                <span>{s.tag}</span>
                {s.count > 0 && (
                  <span className="text-xs text-gray-500">{s.count}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
